'use client';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { classnames } from '@/utils/classnames/classnames';
import { FocusTrap, FocusTrapFeatures, Transition } from '@headlessui/react';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import useHideZendeskWidget from '@/hooks/useHideZendeskWidget';
import Button from '@/components/atoms/button';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import { DrawerProps } from './types';
import useClassNames from './hooks/useClassNames';
import Overlay from '../../atoms/overlay';

const Drawer = ({
  id,
  className = '',
  wrapperClassName,
  headerClassName,
  widthClassName,
  isOpen,
  overlay = true,
  headline,
  subHeadline,
  direction = 'right',
  // EWM-665 (QA round 3) — the Drawer NO LONGER locks body scroll on any
  // viewport. Locking sets `overflow-y: hidden` on the body, which removes the
  // page scrollbar; on a classic (space-taking) scrollbar — Windows / the QA
  // hardware — that shifts the underlying page sideways. AC #2 requires the page
  // to stay scrollable with its scrollbar visible while a tray is open, matching
  // the sign-in drawer's no-lock pattern (EWM-267). The two prior fixes tried to
  // keep a lock on mobile/touch and gate it by device/viewport, which kept
  // reintroducing the shift on touch-capable desktops and in the tablet band —
  // so the lock is now gone everywhere, exactly like sign-in (which never
  // locked and always passed QA). `blockScrolling` is retained ONLY to gate the
  // Zendesk-launcher-hide below (it no longer affects scrolling); the auth
  // drawers pass `blockScrolling={false}` to keep opting out of that hide.
  blockScrolling = true,
  confirmOnDirty = false,
  isDirty = false,
  confirmRequiresChoice = false,
  onClose,
  onDiscard,
  afterLeave,
  footer,
  header,
  initialFocusRef,
  children,
}: React.PropsWithChildren<DrawerProps>) => {
  const translate = useTranslations();
  const headlineId = useId();
  const confirmTitleId = useId();
  const confirmBodyId = useId();
  const returnFocusRef = useRef<Element | null>(null);
  const confirmPanelRef = useRef<HTMLDivElement | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // EWM-849 — portal so the Drawer's `position: fixed` panel/overlay always
  // resolve against the true viewport, regardless of where in the tree it's
  // mounted. Without this, an ancestor that applies a CSS `transform` (e.g.
  // the site header sliding itself off-screen on scroll, EWM-551) becomes
  // the containing block for these fixed descendants instead of the
  // viewport, breaking their positioning.
  //
  // Portal target is the NEAREST `[data-theme]` wrapper, not `document.body` —
  // the app scopes its CSS custom properties (design tokens like `bg-primary`)
  // to `[data-theme='default']` (see app/[locale]/layout.tsx and the
  // AuthDrawerProvider comment there for the same constraint). Escaping all
  // the way to `document.body` would drop the Drawer outside that scope and
  // every token-based class inside it would resolve to nothing.
  //
  // "Nearest" has to be resolved by walking UP from where this Drawer actually
  // sits, not by `document.querySelector('[data-theme]')` — themed wrappers
  // NEST, and querySelector returns the first in document order (the outermost
  // one). Today `app/[locale]/layout.tsx:172` renders a permanent
  // `data-theme="default"` shell around `app/[locale]/[[...slug]]/page.tsx:163`
  // (and `preview/[previewId]/page.tsx:21`), which re-themes per page via
  // `pageFolder.configuration.displayTheme`; `list-switcher` nests a third.
  // A querySelector lookup would portal every Drawer to the outer `default`
  // scope, so a Drawer opened on a non-default-theme page would silently
  // render with default tokens. Hence the inline anchor + `closest()`.
  //
  // The anchor stays in the component's natural DOM position (the only part
  // of the Drawer that does): it's `hidden`, so it takes no layout box and
  // never becomes a flex/grid item, and `closest()` is pure DOM traversal so
  // being display:none doesn't affect it. Resolved once on mount — the
  // anchor's position in the tree is fixed for the component's lifetime, so
  // the nearest themed scope can't change under it. Falls back to
  // `document.body` where no themed wrapper exists (unit tests, Storybook).
  const themeAnchorRef = useRef<HTMLSpanElement | null>(null);
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  useEffect(() => {
    setPortalTarget(themeAnchorRef.current?.closest('[data-theme]') ?? document.body);
  }, []);

  useEffect(() => {
    if (isOpen) {
      returnFocusRef.current = document.activeElement;
    } else if (returnFocusRef.current instanceof HTMLElement) {
      returnFocusRef.current.focus();
      returnFocusRef.current = null;
    }
  }, [isOpen]);

  // EWM-760 (was a setState-in-effect on `[isOpen]`) — adjust-state-during-
  // render: drop any open confirm prompt whenever the drawer closes, so a
  // stale prompt never reappears on the next open. Same guard as the old
  // effect (`if (!isOpen)`) and the same trigger (`isOpen` changing), just
  // applied synchronously in the same render instead of a follow-up effect.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) setShowConfirm(false);
  }

  // EWM-659 — hide the Zendesk chat launcher while a tray is open so its fixed,
  // high-z-index bubble doesn't overlap the drawer's footer controls (e.g. the
  // All Filters "Clear Filters" button). This used to ride on the scroll lock
  // (useScrollBlock hid the launcher as a side effect), but EWM-665 removed the
  // lock entirely — so the Drawer now owns the hide directly, on every viewport.
  // Gated on `blockScrolling` so the auth drawers (blockScrolling={false}) keep
  // their existing opt-out; every other tray hides the launcher while open.
  useHideZendeskWidget(isOpen && blockScrolling);

  // Single close funnel. Esc + scrim (useOnClickOutside), the overlay, and the
  // default/custom close buttons all route through this. A second close gesture
  // while the prompt is open acts as Cancel — handled on the same keyup/click
  // path as the first, so there is no keydown/keyup re-open race.
  const requestClose = useCallback(() => {
    if (showConfirm) {
      // EWM-931 — under `confirmRequiresChoice` the prompt is sticky: Escape and
      // a scrim click are swallowed so the customer must pick Discard or Cancel.
      // Otherwise a second close gesture acts as Cancel (the EWM-307 behaviour
      // the auth drawers depend on).
      if (!confirmRequiresChoice) setShowConfirm(false);
      return;
    }
    if (confirmOnDirty && isDirty) setShowConfirm(true);
    else onClose?.();
  }, [showConfirm, confirmRequiresChoice, confirmOnDirty, isDirty, onClose]);

  const handleCancel = useCallback(() => setShowConfirm(false), []);
  const handleDiscard = useCallback(() => {
    setShowConfirm(false);
    (onDiscard ?? onClose)?.();
  }, [onDiscard, onClose]);

  const { ref } = useOnClickOutside(requestClose);

  // Move focus to the Cancel button (the first button in the panel) when the
  // prompt opens — a safe default that avoids an accidental discard from a
  // stray keypress. Button is a plain FC (no forwardRef), so query the DOM.
  useEffect(() => {
    if (showConfirm) confirmPanelRef.current?.querySelector('button')?.focus();
  }, [showConfirm]);

  const { drawerClassName: drawerWrapperClassName, drawerTransition } = useClassNames(direction);

  const drawerHeaderClassName = classnames(headerClassName, 'px-6 py-4');

  // EWM-188 / EWM-274: responsive widths shared across every Drawer consumer.
  // `relative` (EWM-307) anchors the absolute inset confirm panel below.
  // EWM-494: `widthClassName` lets a single consumer override the width track
  // (default `sm:w-[280px] lg:w-[448px]`) without affecting other drawers.
  const drawerClassName = classnames(
    className,
    'relative flex h-full w-full flex-col bg-white',
    widthClassName ?? 'sm:w-[280px] lg:w-[448px]',
  );

  const renderedHeader = typeof header === 'function' ? header({ requestClose }) : header;

  const drawerBody = (
    <>
      {/* No onClick here: scrim dismissal is owned by useOnClickOutside (mouseup
          outside the dialog), which also owns Esc. Wiring requestClose to BOTH
          the overlay's click AND useOnClickOutside double-fires it on one scrim
          click — the first opens the confirm prompt, the second (now seeing
          showConfirm) cancels it — so the prompt never appears. (EWM-307) */}
      {isOpen && overlay && <Overlay zIndex="z-345" />}

      <Transition
        as="div"
        show={isOpen}
        afterLeave={afterLeave}
        enter="ease-out transition duration-100"
        enterFrom={drawerTransition.enterFrom}
        enterTo={drawerTransition.enterTo}
        leave="ease-in duration-75"
        leaveFrom={drawerTransition.leaveFrom}
        leaveTo={drawerTransition.leaveTo}
        className={drawerWrapperClassName}
      >
        {/* EWM-838 — FocusLock + RestoreFocus (Headless UI's defaults) fight the
            sibling-drawer handoff pattern in AuthDrawerProvider: every auth
            drawer is a permanently-mounted sibling whose `isOpen` toggles, so
            closing one while opening another briefly mounts two FocusTraps at
            once. FocusLock's onBlur treats the newly-focused element in the
            OTHER drawer as focus "escaping" and yanks it back; RestoreFocus
            repeats the steal ~75ms later, when the closing trap unmounts.
            Dropping both leaves InitialFocus (this ticket's fix) and TabLock
            (Tab still can't leave an open drawer). Restoring focus to the
            trigger on a normal close is still handled by this component's
            own `returnFocusRef` effect above — it fires synchronously in the
            same commit as the handoff, before the newly-open drawer's own
            (microtask-deferred) initial-focus grab, so today the new drawer's
            focus lands last and wins. That ordering is an emergent property
            of AuthDrawerProvider's current sibling order + React's effect-
            flush order, not something this component enforces — reordering
            the drawers there (or adding a new one) could invert it with no
            compiler or jsdom signal. The `features` reduction itself is
            covered by a spy-based unit test (index.spec.tsx); the ordering
            claim is not. */}
        <FocusTrap
          className="h-full"
          initialFocus={initialFocusRef}
          features={FocusTrapFeatures.InitialFocus | FocusTrapFeatures.TabLock}
        >
          <div
            ref={ref}
            id={id}
            role="dialog"
            aria-modal="true"
            aria-labelledby={headlineId}
            className={drawerClassName}
          >
            {renderedHeader ? (
              <>
                {/* Hidden heading keeps aria-labelledby on the dialog valid even
                    when consumers render their own visual header. */}
                <h2 id={headlineId} className="sr-only">
                  {headline}
                </h2>
                {renderedHeader}
              </>
            ) : (
              <div className={drawerHeaderClassName}>
                <div className="flex items-center justify-between">
                  <div>
                    {/* EWM-270 — Dan v0 mock specifies `text-22` for the drawer headline. */}
                    <h2 id={headlineId} className="text-22 text-primary font-bold">
                      {headline}
                    </h2>
                    {subHeadline && <p className="text-13 mt-0.5 text-gray-500">{subHeadline}</p>}
                  </div>
                  {/* EWM-490 / EWM-494 — compact, corner-hugging close button per
                      Dan's v0 mock (muted gray, smaller padding than the legacy
                      size="l" ghost which sat inset from the top-right corner). */}
                  <Button
                    variant="ghost"
                    size="s"
                    Icon={XMarkIcon}
                    onClick={requestClose}
                    aria-label={translate('common.close')}
                    className="text-gray-400 hover:text-gray-600"
                  />
                </div>
              </div>
            )}
            <div className="grow overflow-y-auto">{children}</div>
            {footer && <div className="shrink-0">{footer}</div>}

            {showConfirm && (
              <div
                ref={confirmPanelRef}
                className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 p-6"
              >
                <div
                  role="alertdialog"
                  aria-modal="true"
                  aria-labelledby={confirmTitleId}
                  aria-describedby={confirmBodyId}
                  className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl"
                >
                  <h3 id={confirmTitleId} className="text-18 font-bold text-gray-800">
                    {translate('common.unsaved-changes')}
                  </h3>
                  <p id={confirmBodyId} className="text-14 mt-2 text-gray-600">
                    {translate('common.unsaved-changes-warning')}
                  </p>
                  <div className="mt-6 flex justify-end gap-3">
                    <Button variant="secondary" size="m" onClick={handleCancel}>
                      {translate('common.cancel')}
                    </Button>
                    <Button variant="warning" size="m" onClick={handleDiscard}>
                      {translate('common.discard')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </FocusTrap>
      </Transition>
    </>
  );

  return (
    <>
      {/* EWM-849 — the anchor the portal target is resolved from (see the
          `closest()` comment above). `hidden` keeps it out of layout entirely;
          it is the only part of the Drawer that renders in the consumer's own
          DOM position, and the only part that server-renders. */}
      <span ref={themeAnchorRef} hidden aria-hidden="true" />
      {portalTarget &&
        createPortal(
          // EWM-849 — consumers that used to wrap the (inline, non-portaled)
          // Drawer in a responsive-hide className — e.g. MobileMenu's
          // `lg:hidden` — need something in the portaled output itself to carry
          // that class, since the DOM tree no longer runs through their wrapper.
          // No positioning/transform of its own, so it doesn't reintroduce a
          // containing-block trap for the fixed overlay/panel inside. Consumers
          // that pass no `wrapperClassName` (all but MobileMenu today) get no
          // wrapper node at all rather than a bare `<div>` at the portal root.
          wrapperClassName ? <div className={wrapperClassName}>{drawerBody}</div> : drawerBody,
          portalTarget,
        )}
    </>
  );
};

export default Drawer;
